import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import bem from 'utils/bem';
import { docs } from 'components/Footer/credentials';
import Text from 'components/Text';
import { Button } from 'components/Button';
import { SAME_SITE_OPTIONS, VISIBILITY_OPTIONS, defaultCookieConsentName } from './constants';
import { getCookieConsentValue, getLegacyCookieName } from './utils';
import './index.sass';

const { block, el } = bem('cookie-consent');

const defaultCookieConsentProps = {
    hideOnAccept: true,
    hideOnDecline: true,
    visible: VISIBILITY_OPTIONS.BY_COOKIE_VALUE,
    cookieName: defaultCookieConsentName,
    cookieValue: 'true',
    declineCookieValue: 'false',
    setDeclineCookie: true,
    titleText: 'Мы используем файлы Cookie',
    subtitleText: (confidentLink) => <span>Оставаясь на сайте, вы соглашаетесь с их обработкой в соответствии с <a href={ confidentLink } target={ '_blank' }>Политикой конфиденциальности</a></span>,
    buttonText: 'Принять и закрыть',
    declineButtonText: 'I decline',
    debug: false,
    expires: 365,
    enableDeclineButton: false,
    sameSite: SAME_SITE_OPTIONS.LAX,
    acceptOnScroll: false,
    acceptOnScrollPercentage: 25
};
const CookieConsent = ({ debug }) => {

    const [ visible, setVisible ] = useState(false);

    useEffect(() => {
        const { acceptOnScroll } = defaultCookieConsentProps;
        // if cookie undefined or debug
        const cookieValue = getCookieValue();
        if (cookieValue === undefined || debug) {
            setVisible(true);
            // if acceptOnScroll is set to true and (cookie is undefined or debug is set to true), add a listener.
            if (acceptOnScroll) {
                window.addEventListener('scroll', handleScroll, { passive: true });
            }
        }
        return () => {
            removeScrollListener();
        }
    }, []);


    /**
     * Set a persistent accept cookie
     */
    const accept = () => {
        const { cookieName, cookieValue, hideOnAccept } = defaultCookieConsentProps

        setCookie(cookieName, cookieValue);

        if (hideOnAccept) {
            setVisible(false);
            removeScrollListener();
        }
    }

    /**
     * Handle a click on the overlay
     */
    const overlayClick = () => {
        const { acceptOnOverlayClick, onOverlayClick } = defaultCookieConsentProps;

        if (acceptOnOverlayClick) {
            accept();
        }
        onOverlayClick();
    }

    /**
     * Set a persistent decline cookie
     */
    const decline = () => {
        const { cookieName, declineCookieValue, hideOnDecline, onDecline, setDeclineCookie } = defaultCookieConsentProps;

        if (setDeclineCookie) {
            setCookie(cookieName, declineCookieValue);
        }

        onDecline();

        if (hideOnDecline) {
            setVisible(false);
        }
    }

    /**
     * Function to set the consent cookie based on the provided variables
     * Sets two cookies to handle incompatible browsers, more details:
     * https://web.dev/samesite-cookie-recipes/#handling-incompatible-clients
     */
    const setCookie = (cookieName, cookieValue) => {
        const { extraCookieOptions, expires, sameSite } = defaultCookieConsentProps;
        let { cookieSecurity } = defaultCookieConsentProps;

        if (cookieSecurity === undefined) {
            cookieSecurity = window.location ? window.location.protocol === 'https:' : true;
        }

        const cookieOptions = { expires, ...extraCookieOptions, sameSite, secure: cookieSecurity };

        // Fallback for older browsers where can not set SameSite=None,
        // SEE: https://web.dev/samesite-cookie-recipes/#handling-incompatible-clients
        if (sameSite === SAME_SITE_OPTIONS.NONE) {
            Cookies.set(getLegacyCookieName(cookieName), cookieValue, cookieOptions);
        }

        // set the regular cookie
        Cookies.set(cookieName, cookieValue, cookieOptions);
    }

    /**
     * Returns the value of the consent cookie
     * Retrieves the regular value first and if not found the legacy one according
     * to: https://web.dev/samesite-cookie-recipes/#handling-incompatible-clients
     */
    const getCookieValue = () => {
        const { cookieName } = defaultCookieConsentProps;
        return getCookieConsentValue(cookieName);
    }

    /**
     * checks whether scroll has exceeded set amount and fire accept if so.
     */
    const handleScroll = () => {
        const { acceptOnScrollPercentage } = defaultCookieConsentProps;

        // (top / height) - height * 100
        const rootNode = document.documentElement;
        const body = document.body;
        const top = 'scrollTop';
        const height = 'scrollHeight';

        const percentage =
            ((rootNode[top] || body[top]) /
                ((rootNode[height] || body[height]) - rootNode.clientHeight)) *
            100;

        if (percentage > acceptOnScrollPercentage) {
            accept(true);
        }
    };

    const removeScrollListener = () => {
        const { acceptOnScroll } = defaultCookieConsentProps;
        if (acceptOnScroll) {
            window.removeEventListener('scroll', handleScroll);
        }
    };

    const { confidentLink } = docs[window.isRuDomain ? 'ru' : 'kz'];

    return (
        <div className={ block({ visible }) }>
            <div className={ el('container') }>
                <div className={ el('text') }>
                    <div className={ el('title') }>
                        <Text serif>{defaultCookieConsentProps.titleText}</Text>
                    </div>
                    <div className={ el('subtitle') }><Text>{defaultCookieConsentProps.subtitleText(confidentLink)}</Text></div>
                </div>
                <div className={ el('buttons') }>
                    <Button
                        mod={'gold'}
                        key='acceptButton'
                        className={ el('accept') }
                        onClick={accept}
                    >
                        { defaultCookieConsentProps.buttonText }
                    </Button>
                </div>
            </div>
        </div>
    );
}
export default CookieConsent;
